// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * Helpers for traversing a QuestionnaireResponse and projecting answers
 * down to the small set of primitive types our expression evaluator and
 * field validators care about.
 */
/** Walk the response tree and emit `(linkId, items)` for every item, recursively. */
export function* walkResponseItems(response) {
    const stack = [...(response.item ?? [])];
    while (stack.length > 0) {
        const next = stack.pop();
        if (next === undefined)
            break;
        yield next;
        if (next.item !== undefined)
            stack.push(...next.item);
        if (next.answer !== undefined) {
            for (const ans of next.answer) {
                if (ans.item !== undefined)
                    stack.push(...ans.item);
            }
        }
    }
}
/** Collect every QR item keyed by linkId. Later items with the same id win. */
export function indexResponseItems(response) {
    const map = new Map();
    for (const item of walkResponseItems(response)) {
        map.set(item.linkId, item);
    }
    return map;
}
/** Walk every item of a Questionnaire tree. */
export function* walkQuestionnaireItems(items) {
    if (items === undefined)
        return;
    const stack = [...items];
    while (stack.length > 0) {
        const next = stack.pop();
        if (next === undefined)
            break;
        yield next;
        if (next.item !== undefined)
            stack.push(...next.item);
    }
}
export function projectAnswers(answers) {
    if (answers === undefined)
        return [];
    const out = [];
    for (const a of answers) {
        out.push(projectSingleAnswer(a));
    }
    return out;
}
export function projectSingleAnswer(answer) {
    if (answer.valueBoolean !== undefined)
        return answer.valueBoolean;
    if (answer.valueDecimal !== undefined)
        return answer.valueDecimal;
    if (answer.valueInteger !== undefined)
        return answer.valueInteger;
    if (answer.valueDate !== undefined)
        return answer.valueDate;
    if (answer.valueDateTime !== undefined)
        return answer.valueDateTime;
    if (answer.valueTime !== undefined)
        return answer.valueTime;
    if (answer.valueString !== undefined)
        return answer.valueString;
    if (answer.valueUri !== undefined)
        return answer.valueUri;
    if (answer.valueCoding !== undefined)
        return answer.valueCoding.code ?? null;
    if (answer.valueAttachment !== undefined)
        return answer.valueAttachment.url ?? null;
    return undefined;
}
/**
 * Count non-empty answers for a linkId, recursively (handles `repeats`).
 * "Empty" means an answer with no resolvable value[x].
 */
export function countAnswers(response, linkId) {
    let n = 0;
    for (const item of walkResponseItems(response)) {
        if (item.linkId !== linkId)
            continue;
        for (const a of item.answer ?? []) {
            if (projectSingleAnswer(a) !== undefined)
                n += 1;
        }
    }
    return n;
}
/**
 * Return the *first* answer value for a linkId, or undefined if absent.
 * The expression evaluator uses this for `answer(...)`.
 */
export function firstAnswer(response, linkId) {
    for (const item of walkResponseItems(response)) {
        if (item.linkId !== linkId)
            continue;
        for (const a of item.answer ?? []) {
            const v = projectSingleAnswer(a);
            if (v !== undefined)
                return v;
        }
    }
    return undefined;
}
/** Return every answer value for a linkId. */
export function allAnswers(response, linkId) {
    const out = [];
    for (const item of walkResponseItems(response)) {
        if (item.linkId !== linkId)
            continue;
        out.push(...projectAnswers(item.answer));
    }
    return out;
}
//# sourceMappingURL=answers.js.map